import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { history } from "../store";
import { push } from "react-router-redux";
import { withNamespaces } from "react-i18next";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import enGB from "date-fns/locale/en-GB"; // Import the locale
import { fetchCalendarByContract } from "../actions/calendars";
import { format } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import { Grid2x2Check } from "lucide-react";

import AppHeader from "../containers/AppHeader";
import { fetchLockers, postPrebooking } from "../actions/qrs";
import StepBar from "../components/StepBar";
import { QuantityRentalStepper } from "../components/QuantityPickers";

registerLocale("en-GB", enGB); // Register the locale

const TZ = "Europe/Madrid";

function madrid1500ToUtc(dateObj) {
  // Use only the calendar day, ignore the device timezone/time
  const ymd = format(dateObj, "yyyy-MM-dd"); // e.g. "2025-11-29"
  return fromZonedTime(`${ymd} 15:00:00`, TZ); // -> Date; .toISOString() is UTC
}

function applyBusinessRules(totalAmount, daysSelected, businessRules) {
  // Filter only active "MaxDayDiscount" rules
  const activeRules = businessRules.filter(
    (rule) => rule.Active && rule.Type === "MaxDayDiscount"
  );

  if (!activeRules.length) return { total: totalAmount, applied: null };

  // Sort rules by MaxDays descending (so we check the most restrictive first)
  activeRules.sort((a, b) => b.MaxDays - a.MaxDays);

  // Find the first rule that matches daysSelected
  const matchingRule = activeRules.find((rule) => daysSelected >= rule.MaxDays);

  if (matchingRule) {
    const discount = matchingRule.DiscountPercentage;
    const discountedAmount = totalAmount * (1 - discount / 100);
    return { total: discountedAmount, applied: matchingRule };
  }

  return { total: totalAmount, applied: null };
}

class LockerSelectorView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      customer: null,
      step: 1,
      lockers: null,
      prices: null,
      selectedLockerType: null,
      selectedQuantity: 1,
      startDate: new Date(),
      endDate: new Date(),
      selectedInfo: null, // Stores selected information for display
      qrType: "pay",
      errorMessage: false,
      isDurationExceeded: false,
      priceCalendar: {},
      calendarMonth: new Date(),
      limitDays: 13, // Maximum rental duration in days
      selectedPrices: [],
      totalAmount: 0,
      useUnifiedUI: true, // ← turn unified form ON
      isTypeModalOpen: false,
      isQtyModalOpen: false,
      isDatesModalOpen: false,
      isLoading: false,
      activePanel: "type", // 'type' | 'qty' | 'dates' | null
      businessRules: [],
      appliedRule: null,
      panelLeft: 0,
      contract: null,
    };

    this.getLockerPrice = this.getLockerPrice.bind(this);
    this.getLockerType = this.getLockerType.bind(this);
    this.getLockerPicture = this.getLockerPicture.bind(this);

    this.formRef = React.createRef();
    this.typeBtnRef = React.createRef();
    this.qtyBtnRef = React.createRef();
    this.datesStartBtnRef = React.createRef();

    this.handleQuantityChange = this.handleQuantityChange.bind(this);
    this.handleStartDateChange = this.handleStartDateChange.bind(this);
    this.handleEndDateChange = this.handleEndDateChange.bind(this);
    this.handleContinue = this.handleContinue.bind(this);
  }

  openModal = (key) => this.setState({ [key]: true });
  closeModal = (key) => this.setState({ [key]: false });

  openPanel = (panel) => {
    let btnRef = null;

    if (panel === "type") btnRef = this.typeBtnRef;
    if (panel === "qty") btnRef = this.qtyBtnRef;
    if (panel === "dates") btnRef = this.datesStartBtnRef;

    if (btnRef?.current && this.formRef?.current) {
      const btnRect = btnRef.current.getBoundingClientRect();
      const formRect = this.formRef.current.getBoundingClientRect();
      const left = btnRect.left - formRect.left; // ← correct relative offset

      this.setState({
        activePanel: panel,
        panelLeft: left,
      });
    } else {
      this.setState({ activePanel: panel, panelLeft: 0 });
    }
  };
  closePanel = () => this.setState({ activePanel: null });

  componentDidMount() {
    const { zoneSelected, qr } = this.props.location.state;
    const qrtoken = qr.toString() + zoneSelected.toString();

    this.props
      .fetchLockers(qrtoken)
      .then((answer) => {
        const answerLockers = answer.response.answerLockers;
        this.setState(
          {
            lockers: answerLockers.Disponibility,
            prices: answerLockers.Prices,
            customer: answerLockers.Customer,
            qrType: answerLockers.qrType,
          },
          () => {
            // ✅ Wait until DOM updates, then open panel
            setTimeout(() => {
              if (this.typeBtnRef?.current) {
                this.openPanel("type");
              }
            }, 0);
          }
        );
      })
      .catch((error) => {
        console.error(error);
      });
  }

  selectLockerType(lockerType) {
    const lockerPrice = this.getLockerPrice(lockerType);
    const { zoneSelected, descriptionZone } = this.props.location.state;
    const { contract } = this.state;

    this.setState({
      selectedLockerType: lockerType,
      selectedInfo: { lockerType, lockerPrice, zoneSelected, descriptionZone },
      step: 2,
      priceCalendar: {},
      ...(this.state.useUnifiedUI ? { isTypeModalOpen: false } : {}),
    });

    // this.props
    //   .fetchCalendarByContract(contract, lockerType, zoneSelected)
    //   .then((response) => {
    //     const calendarArray = response?.response?.PriceCalendar || [];
    //     const rulesFromOperator = (
    //       response?.response?.BusinessRules || []
    //     ).filter((r) => r && r.Active); // removes null and inactive

    //     // 🔥 Flatten the price calendar into a simple { "YYYY-MM-DD": price } object
    //     const flatPriceMap = {};

    //     calendarArray.forEach((entry) => {
    //       const d = new Date(entry.date); // UTC from DB
    //       const isoDate = d.toLocaleDateString("en-CA", {
    //         timeZone: "Europe/Madrid",
    //       });

    //       const matchedPrice = entry.prices.find(
    //         (p) => p.zone === zoneSelected && p.type === lockerType
    //       );

    //       if (matchedPrice) {
    //         flatPriceMap[isoDate] = matchedPrice.price;
    //       }
    //     });

    //     this.setState({
    //       priceCalendar: flatPriceMap,
    //       businessRules: rulesFromOperator,
    //     });
    //   })
    //   .catch((error) => {
    //     console.error("Error fetching calendar data:", error);
    //   });
  }

  goTo(lockerType, zoneSelected, descriptionZone, e) {
    const { qr, qrMode } = this.props.location.state;
    const { lockers, customer } = this.state;

    const lockerPriceChoosed = this.getLockerPrice(lockerType);

    let hobbitAvailable = false;

    Object.keys(lockers).forEach((lockerKey) => {
      if (lockerKey === lockerType) {
        if (lockers[lockerKey].Hobbit === true) {
          hobbitAvailable = true;
        }
      }
    });

    if (hobbitAvailable) {
      this.props.push("/accessibilityView", {
        zoneSelected: zoneSelected,
        descriptionZone: descriptionZone,
        selectedLocker: lockerType,
        lockerPrice: lockerPriceChoosed,
        lockers: this.state.lockers,
        qr: qr,
        qrType: this.state.qrType,
        qrMode: qrMode,
        customer: customer,
      });
    } else {
      const prebooking = {};

      prebooking.Qr = qr;
      prebooking.LockerSelected = lockerType;
      prebooking.Zone = zoneSelected;
      prebooking.Accessibility = false;
      prebooking.qrMode = qrMode;

      this.props
        .postPrebooking(prebooking)
        .then((answer) => {
          const lockerAssigned =
            answer.response.answerPrebooking.LockerAssigned;
          const lockerPin = answer.response.answerPrebooking.LockerPin;
          const lockerMatricula =
            answer.response.answerPrebooking.LockerMatricula;
          const lockerCustomer =
            answer.response.answerPrebooking.LockerCustomer;
          const lockerCustomerID =
            answer.response.answerPrebooking.LockerCustomerID;
          const lockerMatrixId =
            answer.response.answerPrebooking.LockerMatrixId;

          this.props.push("/PhoneView", {
            selectedLocker: lockerType,
            zoneSelected: zoneSelected,
            selectedAccessibility: false,
            descriptionZone: descriptionZone,
            lockerAssigned: lockerAssigned,
            lockerPrice: lockerPriceChoosed,
            lockerPin: lockerPin,
            lockerMatricula: lockerMatricula,
            lockerCustomer: lockerCustomer,
            lockerCustomerID: lockerCustomerID,
            lockerMatrixId: lockerMatrixId,
            qr: qr,
            qrType: this.state.qrType,
            qrMode: qrMode,
            timeStamp: new Date(),
            customer: customer,
          });
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }

  goBack(e) {
    const { qr } = this.props.location.state;

    history.push(`/qrsale/${qr}`);
  }

  renderMonthGrid(monthDate, isFirstMonth) {
    const {
      priceCalendar,
      startDate,
      endDate,
      calendarMonth,
      selectedQuantity,
    } = this.state;
    const { t, i18n } = this.props;

    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const blanks = Array(
      firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1
    ).fill(null); // Monday start

    const weekDays = [
      t("Lun"),
      t("Mar"),
      t("Mie"),
      t("Jue"),
      t("Vie"),
      t("Sab"),
      t("Dom"),
    ];
    const title = firstDay.toLocaleString("default", {
      month: "long",
      year: "numeric",
    });

    const days = [];

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const iso = date.toLocaleDateString("en-CA", {
        timeZone: "Europe/Madrid",
      });
      const price = priceCalendar[iso];

      const isStart =
        startDate && date.toDateString() === startDate.toDateString();
      const isEnd = endDate && date.toDateString() === endDate.toDateString();
      const inRange =
        startDate && endDate && date > startDate && date < endDate;
      const isPast = this.isBeforeToday(date);

      const isSingleDay =
        isStart &&
        isEnd &&
        startDate?.toDateString() === endDate?.toDateString();

      let className = "rental-calendar-cell";

      if (isSingleDay) {
        className += " selected single";
      } else {
        if (isStart) className += " selected start";
        if (isEnd) className += " selected end";
        if (inRange) className += " in-range";
      }

      if (isPast || !price) className += " disable";

      days.push(
        <div
          key={iso}
          className={className}
          onClick={() => {
            if (!isPast && price) {
              this.handleCalendarDateClick(new Date(date));
            }
          }}
          title={
            price
              ? `Precio: ${(price * selectedQuantity).toFixed(2)} €`
              : "Sin precio"
          }
        >
          <div className="day">{d}</div>
          <div className="price">
            {price && !isPast
              ? `${(price * selectedQuantity).toFixed(2)} €`
              : "-"}
          </div>
        </div>
      );
    }

    return (
      <div className="month-grid">
        <div className="rental-calendar-header">
          <button
            className="nav-btn"
            disabled={this.isCurrentMonthOrBefore(calendarMonth)}
            onClick={() => {
              if (!this.isCurrentMonthOrBefore(calendarMonth)) {
                this.setState({
                  calendarMonth: new Date(
                    calendarMonth.setMonth(calendarMonth.getMonth() - 1)
                  ),
                });
              }
            }}
          >
            &lt;
          </button>
          <h6 className="month-title">
            {title.charAt(0).toUpperCase() + title.slice(1)}
          </h6>
          <button
            className="nav-btn"
            onClick={() =>
              this.setState({
                calendarMonth: new Date(
                  calendarMonth.setMonth(calendarMonth.getMonth() + 1)
                ),
              })
            }
          >
            &gt;
          </button>
        </div>

        <div className="rental-calendar-grid">
          {weekDays.map((day) => (
            <div key={day} className="rental-calendar-cell weekday-header">
              {day}
            </div>
          ))}
          {blanks.map((_, i) => (
            <div key={`blank-${i}`} className="rental-calendar-cell empty" />
          ))}
          {days}
        </div>
      </div>
    );
  }

  renderMonthGridPC(monthDate, isFirstMonth) {
    const {
      priceCalendar,
      startDate,
      endDate,
      calendarMonth,
      selectedQuantity,
    } = this.state;
    const { t, i18n } = this.props;

    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const blanks = Array(
      firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1
    ).fill(null); // Monday start

    const weekDays = [
      t("Lun"),
      t("Mar"),
      t("Mie"),
      t("Jue"),
      t("Vie"),
      t("Sab"),
      t("Dom"),
    ];
    const title = firstDay.toLocaleString("default", {
      month: "long",
      year: "numeric",
    });

    const days = [];

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const iso = date.toLocaleDateString("en-CA", {
        timeZone: "Europe/Madrid",
      });
      const price = priceCalendar[iso];

      const isStart =
        startDate && date.toDateString() === startDate.toDateString();
      const isEnd = endDate && date.toDateString() === endDate.toDateString();
      const inRange =
        startDate && endDate && date > startDate && date < endDate;
      const isPast = this.isBeforeToday(date);

      const isSingleDay =
        isStart &&
        isEnd &&
        startDate?.toDateString() === endDate?.toDateString();

      let className = "rental-calendar-cell";

      if (isSingleDay) {
        className += " selected single";
      } else {
        if (isStart) className += " selected start";
        if (isEnd) className += " selected end";
        if (inRange) className += " in-range";
      }

      if (isPast || !price) className += " disable";

      days.push(
        <div
          key={iso}
          className={className}
          onClick={() => {
            if (!isPast && price) {
              this.handleCalendarDateClick(new Date(date));
            }
          }}
          title={price ? `Precio: ${price * selectedQuantity} €` : "Sin precio"}
        >
          <div className="day">{d}</div>
          <div className="price">
            {price ? `${price * selectedQuantity} €` : "-"}
          </div>
        </div>
      );
    }

    return (
      <div className="month-grid">
        <div className="rental-calendar-header">
          {isFirstMonth && (
            <button
              className="nav-btn"
              disabled={this.isCurrentMonthOrBefore(calendarMonth)}
              onClick={() => {
                if (!this.isCurrentMonthOrBefore(calendarMonth)) {
                  this.setState({
                    calendarMonth: new Date(
                      calendarMonth.setMonth(calendarMonth.getMonth() - 1)
                    ),
                  });
                }
              }}
            >
              &lt;
            </button>
          )}
          <h6 className="month-title">
            {title.charAt(0).toUpperCase() + title.slice(1)}
          </h6>
          {!isFirstMonth && (
            <button
              className="nav-btn"
              onClick={() =>
                this.setState({
                  calendarMonth: new Date(
                    calendarMonth.setMonth(calendarMonth.getMonth() + 1)
                  ),
                })
              }
            >
              &gt;
            </button>
          )}
        </div>

        <div className="rental-calendar-grid">
          {weekDays.map((day) => (
            <div key={day} className="rental-calendar-cell weekday-header">
              {day}
            </div>
          ))}
          {blanks.map((_, i) => (
            <div key={`blank-${i}`} className="rental-calendar-cell empty" />
          ))}
          {days}
        </div>
      </div>
    );
  }

  renderPriceCalendarPC() {
    const { calendarMonth } = this.state;

    const nextMonth = new Date(calendarMonth);
    nextMonth.setMonth(calendarMonth.getMonth() + 1);

    return (
      <div className="rental-dual-calendar-container">
        {this.renderMonthGridPC(calendarMonth, true)}
        {this.renderMonthGridPC(nextMonth, false)}
      </div>
    );
  }

  renderPriceCalendar() {
    const { calendarMonth } = this.state;

    const nextMonth = new Date(calendarMonth);
    nextMonth.setMonth(calendarMonth.getMonth() + 1);

    return (
      <div className="rental-dual-calendar-container">
        {this.renderMonthGrid(calendarMonth, true)}
      </div>
    );
  }

  handleCalendarDateClick = (clickedDate) => {
    const { startDate, endDate, priceCalendar, selectedQuantity } = this.state;

    if (startDate && clickedDate.toDateString() === startDate.toDateString()) {
      this.setState({
        startDate: null,
        endDate: null,
        selectedPrices: [],
        totalAmount: 0,
        isDurationExceeded: false,
      });
      return;
    } else if (!startDate || clickedDate < startDate) {
      const iso = clickedDate.toLocaleDateString("en-CA", {
        timeZone: "Europe/Madrid",
      });
      const base = priceCalendar[iso] || 0;
      const dayPrice = base * (selectedQuantity || 1);

      this.setState({
        startDate: clickedDate,
        endDate: clickedDate, // same day
        isDurationExceeded: false,
        selectedPrices: base
          ? [{ date: new Date(clickedDate), price: dayPrice }]
          : [],
        totalAmount: base ? dayPrice : 0,
      });
      return;
    } else {
      const diff = Math.ceil((clickedDate - startDate) / (1000 * 60 * 60 * 24));
      const tooLong = diff > this.state.limitDays;

      if (!tooLong) {
        const selectedPrices = [];
        let totalAmount = 0;

        for (
          let d = new Date(startDate);
          d <= clickedDate;
          d.setDate(d.getDate() + 1)
        ) {
          const iso = d.toLocaleDateString("en-CA", {
            timeZone: "Europe/Madrid",
          });
          const price = priceCalendar[iso];
          if (price) {
            const dayPrice = price * selectedQuantity;
            selectedPrices.push({ date: new Date(d), price: dayPrice });
            totalAmount += dayPrice;
          }
        }

        // ✅ Apply business rules discount here
        const daysSelected = selectedPrices.length;
        const { total, applied } = applyBusinessRules(
          totalAmount,
          daysSelected,
          this.state.businessRules
        );

        this.setState({
          endDate: clickedDate,
          isDurationExceeded: tooLong,
          selectedPrices,
          totalAmount: total,
          appliedRule: applied,
        });
      } else {
        this.setState({ endDate: clickedDate, isDurationExceeded: true });
      }
    }
  };

  handleQuantityChange(event) {
    this.setState({
      selectedQuantity: event.target.value,
      startDate: null,
      endDate: null,
      selectedPrices: [],
      totalAmount: 0,
      isDurationExceeded: false,
    });
  }

  handleStartDateChange(date) {
    const { endDate } = this.state;
    const localDate = new Date(date.setHours(15, 0, 0, 0)); // Set to the start of the day in local time

    if (endDate) {
      const differenceInDays = Math.ceil(
        (endDate - localDate) / (1000 * 60 * 60 * 24)
      );
      this.setState({
        startDate: localDate,
        isDurationExceeded: differenceInDays >= this.state.limitDays, // Show message if duration exceeds 14 days
      });
    } else {
      this.setState({ startDate: localDate });
    }
  }

  handleEndDateChange(date) {
    const { startDate } = this.state;
    const localDate = new Date(date.setHours(15, 0, 0, 0)); // Set to the start of the day in local time

    if (startDate) {
      const differenceInDays = Math.ceil(
        (localDate - startDate) / (1000 * 60 * 60 * 24)
      );
      this.setState({
        endDate: localDate,
        isDurationExceeded: differenceInDays >= this.state.limitDays, // Show message if duration exceeds 14 days
      });
    } else {
      this.setState({ endDate: localDate });
    }
  }

  isAfterCutoffTime() {
    const nowInSpain = new Date().toLocaleString("en-US", {
      timeZone: "Europe/Madrid",
    });
    const currentHourInSpain = new Date(nowInSpain).getHours();
    return currentHourInSpain >= 18;
  }

  handleContinue() {
    if (this.state.isLoading) return;
    this.setState({ isLoading: true });
    const { qr, qrMode, zoneSelected, descriptionZone } = this.props.location.state;
    const { qrType, lockers, customer, selectedLockerType } = this.state;

    const lockerPriceChoosed = this.getLockerPrice(selectedLockerType);

    let hobbitAvailable = false;

    Object.keys(lockers).forEach((lockerKey) => {
      if (lockerKey === selectedLockerType) {
        if (lockers[lockerKey].Hobbit === true) {
          hobbitAvailable = true;
        }
      }
    });

    if (hobbitAvailable) {
      this.props.push("/accessibilityView", {
        zoneSelected: zoneSelected,
        descriptionZone: descriptionZone,
        selectedLocker: selectedLockerType,
        lockerPrice: lockerPriceChoosed,
        lockers: this.state.lockers,
        qr: qr,
        qrType: qrType,
        qrMode: qrMode,
        customer: customer,
      });
    } else {
      const prebooking = {};

      prebooking.Qr = qr;
      prebooking.LockerSelected = selectedLockerType;
      prebooking.Zone = zoneSelected;
      prebooking.Accessibility = false;
      prebooking.qrMode = qrMode;

      this.props
        .postPrebooking(prebooking)
        .then((answer) => {
          const lockerAssigned =
            answer.response.answerPrebooking.LockerAssigned;
          const lockerPin = answer.response.answerPrebooking.LockerPin;
          const lockerMatricula =
            answer.response.answerPrebooking.LockerMatricula;
          const lockerCustomer =
            answer.response.answerPrebooking.LockerCustomer;
          const lockerCustomerID =
            answer.response.answerPrebooking.LockerCustomerID;
          const lockerMatrixId =
            answer.response.answerPrebooking.LockerMatrixId;

          this.props.push("/PhoneView", {
            selectedLocker: selectedLockerType,
            zoneSelected: zoneSelected,
            selectedAccessibility: false,
            descriptionZone: descriptionZone,
            lockerAssigned: lockerAssigned,
            lockerPrice: lockerPriceChoosed,
            lockerPin: lockerPin,
            lockerMatricula: lockerMatricula,
            lockerCustomer: lockerCustomer,
            lockerCustomerID: lockerCustomerID,
            lockerMatrixId: lockerMatrixId,
            qr: qr,
            qrType: qrType,
            qrMode: qrMode,
            timeStamp: new Date(),
            customer: customer,
          });
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }

  getLockerType(type) {
    const { t, i18n } = this.props;

    switch (type) {
      case "BNS":
        return "Standard";
      case "CNS":
        return "Standard Carga";
      case "BWS":
        return "Standard XL";
      case "BNM":
        return "Medium";
      case "CNM":
        return "Medium Carga";
      case "BWM":
        return "Medium XL";
      case "CWM":
        return "Medium Carga XL";
      case "BNL":
        return "Large";
      case "CNL":
        return "Large Carga";
      case "BWL":
        return "Large XL";
      case "CWL":
        return "Large Carga XL";
      case "BNXL":
        return "Large";
      case "CNXL":
        return "Large Carga";
      case "BWXL":
        return "Large XL";
      case "CWXL":
        return "Large Carga XL";
      case "BN2XL":
        return "Large";
      case "CN2XL":
        return "Large Carga";
      case "BW2XL":
        return "Large XL";
      case "CW2XL":
        return "Large Carga XL";
      case "BN3XL":
        return "Ski";
      case "CN3XL":
        return "Ski Carga";
      case "BW3XL":
        return "Ski XL";
      case "BW3XL":
        return "Ski Carga XL";
      default:
        return "None";
    }
  }

  getLockerPicture(type) {
    return `/images/measures/locker_final_${type}.png`;
  }

  getLockerPcPicture(type) {
    return `/images/measures/locker_final_${type}.png`;
  }

  getLockerPrice(type) {
    const { prices } = this.state;
    const lockerPriceObj = prices.find(
      (price) => price.item === `LOCKER${type}`
    );

    let lockerPrice = 0;

    if (lockerPriceObj) {
      lockerPrice = Number(lockerPriceObj.price);
    }

    return lockerPrice.toFixed(2);
  }

  renderLockers() {
    const { t } = this.props;
    const { lockers, prices } = this.state;
    const { zoneSelected, descriptionZone } = this.props.location.state;
    const lockerDivs = [];

    const orderedLockerKeys = [
      "BNS",
      "CNS",
      "BWS",
      "CWS",
      "BNM",
      "CNM",
      "BWM",
      "CWM",
      "BNL",
      "CNL",
      "BWL",
      "CWL",
      "BNXL",
      "CNXL",
      "BWXL",
      "CWXL",
      "BN2XL",
      "CN2XL",
      "BW2XL",
      "CW2XL",
      "BN3XL",
      "CN3XL",
      "BW3XL",
      "CW3XL",
    ];

    if (!lockers || !prices) {
      return (
        <div className="spinner">
          <img src="/images/logo.png" alt="Logo" />
        </div>
      );
    } else {
      orderedLockerKeys.forEach((lockerKey) => {
        const locker = lockers[lockerKey];
        if (!locker) return;

        lockerDivs.push(
          <div className="col-xs-12 titleh5" key={lockerKey}>
            <div
              className="lockButton"
              onClick={() => this.selectLockerType(lockerKey)}
            >
              <div className="row">
                <div className="colLockers">
                  <img
                    src={this.getLockerPicture(lockerKey)}
                    alt={"locker_" + lockerKey}
                    className="lockerRentalSmallImage"
                  />
                </div>
                <div className="col m-auto">
                  <h6 className="titleLockers">{t("Taquilla")}</h6>
                  <h5 className="col lockerTextDescription">
                    {this.getLockerType(lockerKey)}
                  </h5>
                  <div className="pricesLockers">
                    {this.getLockerPrice(lockerKey)} €
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      });

      if (lockerDivs.length === 0) {
        return (
          <div>
            <div className="col-xs-12 titleh5">
              <div
                className="lockSoldOutButton"
                onClick={(e) => this.goBack(e)}
              >
                <h5>{t("TAQUILLAS AGOTADAS")}</h5>
                <h6>{t("Haz click aquí para volver a selección de zonas")}</h6>
                <div className="lockSoldOutImgContainer">
                  <img
                    className="lockSoldOutImg"
                    src="/images/locker_BNS.png"
                    alt="Locker"
                  />
                  <img
                    className="lockSoldOutCross"
                    src="/images/cross.png"
                    alt="Cross"
                  />
                </div>
              </div>
            </div>
          </div>
        );
      }

      return <div>{lockerDivs}</div>;
    }
  }

  renderLockersPc() {
    const { t } = this.props;
    const { lockers, prices } = this.state;
    const { zoneSelected, descriptionZone } = this.props.location.state;
    const lockerDivs = [];

    const orderedLockerKeys = [
      "BNS",
      "CNS",
      "BWS",
      "CWS",
      "BNM",
      "CNM",
      "BWM",
      "CWM",
      "BNL",
      "CNL",
      "BWL",
      "CWL",
      "BNXL",
      "CNXL",
      "BWXL",
      "CWXL",
      "BN2XL",
      "CN2XL",
      "BW2XL",
      "CW2XL",
      "BN3XL",
      "CN3XL",
      "BW3XL",
      "CW3XL",
    ];

    if (!lockers || !prices) {
      return (
        <div className="spinner">
          <img src="/images/logo.png" alt="Logo" />
        </div>
      );
    } else {
      orderedLockerKeys.forEach((lockerKey) => {
        const locker = lockers[lockerKey];
        if (!locker) return;

        lockerDivs.push(
          <div
            key={lockerKey}
            className="lockButton"
            onClick={(e) => {
              this.selectLockerType(lockerKey);
              // this.openPanel("qty");
            }}
          >
            <img
              src={this.getLockerPcPicture(lockerKey)}
              alt={`locker_${lockerKey}`}
              className="lockerRentalSmallImage"
            />
            <div className="col titleLockersRental">
              <h6>{t("Taquilla")}</h6>
              <h5>{this.getLockerType(lockerKey)}</h5>
            </div>
            <div className="pricesLockers">{this.getLockerPrice(lockerKey)} €</div>
          </div>
        );
      });

      if (lockerDivs.length === 0) {
        return (
          <div>
            <div className="col-xs-12 titleh5">
              <div
                className="lockSoldOutButton"
                onClick={(e) => this.goBack(e)}
              >
                <h5>{t("TAQUILLAS AGOTADAS")}</h5>
                <h6>{t("Haz click aquí para volver a selección de zonas")}</h6>
                <div className="lockSoldOutImgContainer">
                  <img
                    className="lockSoldOutImg"
                    src="/images/locker_BNS.png"
                    alt="Locker"
                  />
                  <img
                    className="lockSoldOutCross"
                    src="/images/cross.png"
                    alt="Cross"
                  />
                </div>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div style={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
          <div
            className="row justify-content-center"
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "1rem",
              padding: "1rem 0",
              minWidth: "max-content",
            }}
          >
            {lockerDivs}
          </div>
        </div>
      );
    }
  }

  isBeforeToday(date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Remove time
    return date < today;
  }

  isBeforeStart(date) {
    const { startDate } = this.state;

    const today = new Date(startDate);
    today.setHours(0, 0, 0, 0); // Remove time
    return date < today;
  }

  isCurrentMonthOrBefore(date) {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();

    return date.getFullYear() <= year && date.getMonth() <= month;
  }

  renderUnifiedForm(mode = "mobile") {
    const { t } = this.props;
    const { zoneSelected, descriptionZone } = this.props.location.state;
    const {
      selectedLockerType,
      selectedQuantity,
      startDate,
      endDate,
      isDurationExceeded,
      totalAmount,
      appliedRule,
    } = this.state;

    const qty = selectedQuantity == null ? 1 : Number(selectedQuantity);
    const canContinue =
      selectedLockerType &&
      selectedQuantity &&
      startDate &&
      !isDurationExceeded;

    return (
      <div className="unified-form">
        <Grid2x2Check className="rental-phoneIcon" strokeWidth={1} />
        <div className="lockTitle">
          <span className="text-vw-semidark">
            {t("Rellena los datos para ver disponibilidad")}
          </span>
        </div>

        {/* ZONE (read-only) */}
        <div className="rental-uf-row">
          <button type="button" className="rental-uf-btn" disabled>
            <label className="rental-uf-label">{t("Zona")}</label>
            {zoneSelected} - {descriptionZone}
          </button>
        </div>

        {/* TYPE */}
        <div className="rental-uf-row">
          <button
            type="button"
            className="rental-uf-btn"
            onClick={() => this.openModal("isTypeModalOpen")}
          >
            <label className="rental-uf-label">{t("Tipo")}</label>
            {selectedLockerType
              ? this.getLockerType(selectedLockerType)
              : t("Selecciona...")}
          </button>
        </div>

        {/* QUANTITY */}
        <div className="rental-uf-row">
          <button
            type="button"
            className="rental-uf-btn"
            onClick={() => this.openModal("isQtyModalOpen")}
            disabled={(!selectedLockerType) || true}
          >
            <label className="rental-uf-label">{t("Cantidad")}</label>
            {qty} {qty === 1 ? " " + t("taquilla") : " " + t("taquillas")}
          </button>
        </div>

        {/* DATES */}
        <div className="rental-uf-row">
          <div className="d-flex">
            <div className="col">
              <button
                type="button"
                className="rental-uf-btn"
                onClick={() => this.openModal("isDatesModalOpen")}
                disabled={(!selectedLockerType) || true}
              >
                <label className="rental-uf-label">{t("Inicio")}</label>
                {startDate
                  ? `${startDate.toLocaleDateString()}`
                  : t("DD/MM/YYYY")}
              </button>
            </div>

            <vr class="vr"></vr>

            <div className="col">
              <button
                type="button"
                className="rental-uf-btn"
                onClick={() => this.openModal("isDatesModalOpen")}
                disabled={(!selectedLockerType || !startDate) || true}
              >
                <label className="rental-uf-label">{t("Fin")}</label>
                {endDate ? `${endDate.toLocaleDateString()}` : t("DD/MM/YYYY")}
              </button>
            </div>
          </div>
        </div>

        {/* TOTAL 
          <div className="rental-uf-row">
            <label className="rental-uf-label">{t('Importe')}</label>
            <button
              type="button"
              className="rental-uf-btn"
            >
              {totalAmount.toFixed(2)} €
            </button>
          </div>*/}

        {/* ERROR MESSAGE */}
        {this.state.errorMessage && (
          <div className="text-danger text-center">
            {t(
              "LO SENTIMOS PERO ESTAS FECHAS NO ESTÁN DISPONIBLES, PRUEBA OTRAS U OTRO TIPO DE TAQUILLAS."
            )}
          </div>
        )}

        {/*DISCOUNT*/}
        {appliedRule && (
          <span className="text-center">
            {t("Descuento aplicado: {{discount}}% (desde los {{days}} días)", {
              discount: appliedRule.DiscountPercentage,
              days: appliedRule.MaxDays,
            })}
          </span>
        )}

        {/* CONTINUE */}
        <div className="uf-actions">
          <button
            className={`lockerContinueButton ${!canContinue || this.state.isLoading ? "disabled" : ""}`}
            disabled={!canContinue || this.state.isLoading}
            onClick={() => this.handleContinue()}
          >
            {this.state.isLoading ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            ) : (
              <>
                {t("Continuar")}
                {t(" por ")}
                <br />
                {Number(this.getLockerPrice(selectedLockerType)).toFixed(2)}
                {" €"}
              </>
            )}
          </button>
        </div>

        {this.renderUnifiedModals()}
      </div>
    );
  }

  renderUnifiedModals() {
    const { t } = this.props;
    const {
      isTypeModalOpen,
      isQtyModalOpen,
      isDatesModalOpen,
      selectedQuantity,
      startDate,
      endDate,
      isDurationExceeded,
      appliedRule,
    } = this.state;
    const qty = selectedQuantity == null ? 1 : Number(selectedQuantity);

    return (
      <>
        {/* TYPE MODAL */}
        {isTypeModalOpen && (
          <div
            className="uf-modal-overlay"
            onClick={() => this.closeModal("isTypeModalOpen")}
          >
            <div className="uf-modal" onClick={(e) => e.stopPropagation()}>
              <div className="uf-modal-header">
                <h5 className="text-vw-dark">
                  {t("Selecciona el tipo de taquilla")}
                </h5>
              </div>
              <div className="uf-modal-body">
                {/* Reuse your locker list UI */}
                {this.renderLockers()}
              </div>
              <div className="uf-modal-footer"></div>
            </div>
          </div>
        )}

        {/* QTY MODAL */}
        {isQtyModalOpen && (
          <div
            className="uf-modal-overlay"
            onClick={() => this.closeModal("isQtyModalOpen")}
          >
            <div className="uf-modal" onClick={(e) => e.stopPropagation()}>
              <div className="uf-modal-header">
                <h5 className="text-vw-dark">{t("Cantidad de taquillas")}</h5>
              </div>
              <div className="uf-modal-body">
                <QuantityRentalStepper
                  min={1}
                  max={10}
                  value={qty}
                  label={t("Selecciona cuántas taquillas necesitas")}
                  onChange={(v) =>
                    this.setState({
                      selectedQuantity: v,
                      startDate: null,
                      endDate: null,
                      selectedPrices: [],
                      totalAmount: 0,
                      isDurationExceeded: false,
                      appliedRule: null,
                    })
                  }
                />
                <span>
                  {selectedQuantity}{" "}
                  {selectedQuantity > 1
                    ? t("taquillas seleccionadas")
                    : t("taquilla seleccionada")}
                </span>
              </div>
              <div className="uf-modal-footer">
                <button
                  className="lockerContinueButton"
                  onClick={() => this.closeModal("isQtyModalOpen")}
                >
                  {t("Confirmar")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* DATES MODAL */}
        {isDatesModalOpen && (
          <div
            className="uf-modal-overlay"
            onClick={() => this.closeModal("isDatesModalOpen")}
          >
            <div
              className="uf-modal text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="uf-modal-header">
                <h5 className="text-vw-dark">{t("Selecciona las fechas")}</h5>
              </div>
              <div className="uf-modal-body">
                {/* Reuse your price calendar (mobile version here) */}
                {this.renderPriceCalendar()}
                {isDurationExceeded && (
                  <div className="text-center text-danger mt-2">
                    {t(
                      `El alquiler online está limitado a 14 días consecutivos...`
                    )}
                  </div>
                )}
              </div>
              <div className="uf-modal-footer">
                <div className="row">
                  <div
                    className="col"
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    <label className="rental-uf-label">{t("Inicio")}</label>
                    <span>
                      {startDate
                        ? `${startDate.toLocaleDateString()}`
                        : t("DD/MM/YYYY")}
                    </span>
                  </div>
                  <vr class="bg-vw-light"></vr>
                  <div
                    className="col"
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    <label className="rental-uf-label">{t("Fin")}</label>
                    <span>
                      {endDate
                        ? `${endDate.toLocaleDateString()}`
                        : t("DD/MM/YYYY")}
                    </span>
                  </div>
                </div>
                <hr></hr>
                {appliedRule && (
                  <span>
                    {t(
                      "Descuento aplicado: {{discount}}% (desde los {{days}} días)",
                      {
                        discount: appliedRule.DiscountPercentage,
                        days: appliedRule.MaxDays,
                      }
                    )}
                  </span>
                )}
                <br></br>
                <button
                  className="lockerContinueButton"
                  disabled={!startDate || isDurationExceeded}
                  onClick={() => this.closeModal("isDatesModalOpen")}
                >
                  {t("Confirmar")}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  renderUnifiedFormPc() {
    const { t } = this.props;
    const { zoneSelected, descriptionZone } = this.props.location.state;
    const {
      selectedLockerType,
      selectedQuantity,
      startDate,
      endDate,
      isDurationExceeded,
      totalAmount,
      appliedRule,
    } = this.state;

    const qty = selectedQuantity == null ? 1 : Number(selectedQuantity);
    const canContinue =
      selectedLockerType &&
      selectedQuantity &&
      startDate &&
      !isDurationExceeded;

    return (
      <>
        {/* FORM ROW */}
        <div className="rental-unified-form" ref={this.formRef}>
          <div
            className="rental-uf-row"
            style={{ display: "flex", gap: "12px" }}
          >
            <Grid2x2Check
              className="rental-phoneIcon m-0 mr-5"
              strokeWidth={1}
            />

            <vr class="vr"></vr>

            {/* ZONE */}
            <div style={{ flex: 1 }}>
              <button type="button" className="rental-uf-btn" disabled>
                <span className="rental-uf-label">{t("Zona")}</span>
                {zoneSelected} - {descriptionZone}
              </button>
            </div>

            <vr class="vr"></vr>

            {/* TYPE */}
            <div style={{ flex: 1 }}>
              <button
                ref={this.typeBtnRef}
                type="button"
                className="rental-uf-btn"
                onClick={() => this.openPanel("type")}
              >
                <span className="rental-uf-label">{t("Tipo")}</span>
                {selectedLockerType
                  ? this.getLockerType(selectedLockerType)
                  : t("Selecciona...")}
              </button>
            </div>

            <vr class="vr"></vr>

            {/* QUANTITY */}
            <div style={{ flex: 1 }}>
              <button
                ref={this.qtyBtnRef}
                type="button"
                className="rental-uf-btn"
                onClick={() => this.openPanel("qty")}
                disabled={(!selectedLockerType) || true}
              >
                <span className="rental-uf-label">{t("Cantidad")}</span>
                {qty} {qty === 1 ? " " + t("taquilla") : " " + t("taquillas")}
              </button>
            </div>

            <vr class="vr"></vr>

            {/* DATES */}
            <div style={{ flex: 1 }}>
              <button
                ref={this.datesStartBtnRef}
                type="button"
                className="rental-uf-btn"
                onClick={() => this.openPanel("dates")}
                disabled={(!selectedLockerType) || true}
              >
                <span className="rental-uf-label">{t("Inicio")}</span>
                {startDate
                  ? `${startDate.toLocaleDateString()}`
                  : t("DD/MM/YYYY")}
              </button>
            </div>

            <vr class="vr"></vr>

            <div style={{ flex: 1 }}>
              <button
                type="button"
                className="rental-uf-btn"
                onClick={() => this.openPanel("dates")}
                disabled={(!selectedLockerType || !startDate)  || true}
              >
                <span className="rental-uf-label">{t("Fin")}</span>
                {endDate ? `${endDate.toLocaleDateString()}` : t("DD/MM/YYYY")}
              </button>
            </div>

            {/*DISCOUNT*/}
            {appliedRule && (
              <span
                style={{
                  border: "1px solid #00000040",
                  padding: "10px",
                  borderRadius: "10px",
                  maxWidth: "200px",
                  maxHeight: "4vw",
                  marginTop: "0.5rem",
                }}
              >
                {t(
                  "Descuento aplicado: {{discount}}% (desde los {{days}} días)",
                  {
                    discount: appliedRule.DiscountPercentage,
                    days: appliedRule.MaxDays,
                  }
                )}
              </span>
            )}

            <vr class="vr"></vr>

            {/* CONTINUE BUTTON */}
            <button
              className={`lockerContinueButton ${
                !canContinue || this.state.isLoading ? "disabled" : ""
              }`}
              style={{ margin: "0" }}
              disabled={!canContinue || this.state.isLoading}
              onClick={() => this.handleContinue()}
            >
              {this.state.isLoading ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              ) : (
                <>
                  {t("Continuar")}
                  {t(" por ")}
                  {Number(this.getLockerPrice(selectedLockerType)).toFixed(2)}
                  {" €"}
                </>
              )}
            </button>
          </div>
        </div>

        {/* ERROR MESSAGE */}
        {this.state.errorMessage && (
          <div className="text-danger text-center">
            {t(
              "LO SENTIMOS PERO ESTAS FECHAS NO ESTÁN DISPONIBLES, PRUEBA OTRAS U OTRO TIPO DE TAQUILLAS."
            )}
          </div>
        )}

        {/* INLINE PANELS */}
        {this.renderUnifiedPanels()}
      </>
    );
  }

  renderUnifiedPanels(mode) {
    const { t } = this.props;
    const {
      isTypeModalOpen,
      isQtyModalOpen,
      isDatesModalOpen,
      selectedQuantity,
      startDate,
      endDate,
      isDurationExceeded,
      activePanel,
      appliedRule,
      panelLeft,
    } = this.state;
    const qty = selectedQuantity == null ? 1 : Number(selectedQuantity);

    return (
      <>
        {activePanel === "type" && (
          <div className="rental-uf-panel text-center show">
            <h5 className="text-vw-dark">
              {t("Selecciona el tipo de taquilla")}
            </h5>
            {this.renderLockersPc()}
          </div>
        )}

        {activePanel === "qty" && (
          <div className="rental-uf-panel text-center show">
            <div className="rental-uf-modal-header">
              <h5 className="text-vw-dark">{t("Cantidad de taquillas")}</h5>
            </div>
            <div className="uf-modal-body">
              <QuantityRentalStepper
                min={1}
                max={10}
                value={qty}
                label={t("Selecciona cuántas taquillas necesitas")}
                onChange={(v) =>
                  this.setState({
                    selectedQuantity: v,
                    startDate: null,
                    endDate: null,
                    selectedPrices: [],
                    totalAmount: 0,
                    isDurationExceeded: false,
                    appliedRule: null,
                  })
                }
              />
            </div>
            <span>
              {selectedQuantity}{" "}
              {selectedQuantity > 1
                ? t("taquillas seleccionadas")
                : t("taquilla seleccionada")}
            </span>
          </div>
        )}

        {activePanel === "dates" && (
          <div className="rental-uf-panel text-center show">
            <h5 className="text-vw-dark">{t("Selecciona las fechas")}</h5>
            {isDurationExceeded && (
              <div className="text-center text-danger mt-2">
                {t(
                  `El alquiler online está limitado a 14 días consecutivos...`
                )}
              </div>
            )}
            {this.renderPriceCalendarPC()}
          </div>
        )}
      </>
    );
  }

  renderStepContent() {
    const { t } = this.props;
    const {
      step,
      lockers,
      selectedLockerType,
      selectedQuantity,
      startDate,
      endDate,
      isDurationExceeded,
      useUnifiedUI,
    } = this.state;

    if (useUnifiedUI) {
      // Single big form for mobile
      return this.renderUnifiedForm("mobile");
    }
  }

  renderStepContentPc() {
    const { t } = this.props;
    const {
      step,
      lockers,
      selectedLockerType,
      selectedQuantity,
      startDate,
      endDate,
      isDurationExceeded,
      useUnifiedUI,
    } = this.state;

    if (useUnifiedUI) {
      // Single big form for pc
      return this.renderUnifiedFormPc();
    }
  }

  render() {
    const { t } = this.props;
    const { lockers, prices } = this.state;

    if (!lockers || !prices) {
      return (
        <div className="spinner">
          <img src="/images/logo.png" alt="Logo" />
        </div>
      );
    }

    return (
      <div className="dailyBackground">
        <AppHeader />
        <div className="lockerContainer smartphone">
          <br></br>
          <h3 className="lockTitle text-center">
            <span className="text-vw-dark">{t("RESERVA DE TAQUILLA")}</span>
          </h3>
          {this.renderStepContent()}
          <br></br>
          <StepBar step={2} />
        </div>

        <div className="lockerContainer pc">
          <h3 className="mt-4 text-center">
            <span className="text-vw-dark">{t("RESERVA DE TAQUILLA")}</span>
          </h3>
          {/*
          <h6 className="text-left">
            <span className="text-vw-semidark">
              {t('Rellena los datos para ver disponibilidad')}
            </span>
          </h6>
          */}
          <br></br>
          {this.renderStepContentPc()}
          <br></br>
          <StepBar step={2} />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  auth: true,
  lockers: state.lockers,
  prices: state.prices,
});

const mapDispatchToProps = (dispatch) => ({
  push: bindActionCreators(push, dispatch),
  fetchLockers: bindActionCreators(fetchLockers, dispatch),
  postPrebooking: bindActionCreators(postPrebooking, dispatch),
});

export default withNamespaces()(
  connect(mapStateToProps, mapDispatchToProps)(LockerSelectorView)
);
